/**
 * TreeNode — recursive service tree node (+ manual tree-test mode, TASK-260).
 * Moved out of ServiceManagementPage.tsx (KAN-157 god-component split).
 */
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import type { Service, TreeTestSelection } from '@/services/api/services';
import { LeafTestInputs } from './LeafTestInputs';
import { ServiceTypeMarker } from './ServiceTypeMarker';

// ── Manual tree-test mode (TASK-260) ──────────────────────────────────────
//
// Shared context passed down the recursive tree when the test mode is on.
// A node uses it to render its checkbox/radio + inline input fields and to
// highlight the ancestor chain of selected leaves.
export interface TreeTestCtx {
  /** Picked leaves keyed by service_id (selection payload sent to the API). */
  selections: Map<number, TreeTestSelection>;
  /** Service ids on the path of any selected leaf (visual ancestor highlight). */
  highlightedIds: Set<number>;
  /** Toggle a plain leaf (checkbox). */
  onToggleLeaf: (service: Service, checked: boolean) => void;
  /** Pick one branch under a SWITCH selector (radio), keyed by parent id. */
  onSelectorChoice: (parentId: number, child: Service) => void;
  /** Patch a selection's inputs / variant / quantity / items. */
  onPatch: (serviceId: number, patch: Partial<TreeTestSelection>) => void;
}

// A leaf is a node with no children that carries a real calculation method.
function isSelectableLeaf(
  service: Service,
  childrenByParent: Map<number, Service[]>
): boolean {
  const hasChildren = (childrenByParent.get(service.id) || []).length > 0;
  if (hasChildren) return false;
  return (
    service.calculation_method === 'calculator' ||
    service.calculation_method === 'classifier' ||
    service.calculation_method === 'pricelist'
  );
}

// A selector is a parent whose aggregation formula is a SWITCH over its
// children — exactly one branch participates in the result.
function isSelectorService(service: Service): boolean {
  const formula = service.aggregation_formula || '';
  return /SWITCH/i.test(formula);
}

export function TreeNode({
  service,
  childrenByParent,
  selectedId,
  onSelect,
  expanded,
  onToggle,
  onAddChild,
  ancestors,
  depth,
  test,
  parentIsSelector = false,
}: {
  service: Service;
  childrenByParent: Map<number, Service[]>;
  selectedId: number | null;
  onSelect: (service: Service) => void;
  expanded: Set<number>;
  onToggle: (id: number) => void;
  onAddChild: (parentId: number) => void;
  /** Set of ancestor ids on the current path — cycle protection. */
  ancestors: Set<number>;
  depth: number;
  /** Manual tree-test context (TASK-260); undefined when test mode is off. */
  test?: TreeTestCtx;
  /** True when this node's parent is a SWITCH selector → render a radio. */
  parentIsSelector?: boolean;
}): JSX.Element | null {
  // Cycle guard: if this id is already on the path, stop recursing.
  if (ancestors.has(service.id)) return null;

  const children = childrenByParent.get(service.id) || [];
  const hasChildren = children.length > 0;
  const isExpanded = expanded.has(service.id);
  const isSelected = selectedId === service.id;
  const isCalculator = service.calculation_method === 'calculator';

  const nextAncestors = new Set(ancestors);
  nextAncestors.add(service.id);

  // ── Manual tree-test mode (TASK-260) ────────────────────────────────────
  const testActive = test !== undefined;
  const isLeaf = testActive && isSelectableLeaf(service, childrenByParent);
  const selection = test?.selections.get(service.id);
  const isPicked = selection !== undefined;
  const isHighlighted = testActive && (test?.highlightedIds.has(service.id) ?? false);
  // This node IS a selector → its children render as mutually-exclusive radios.
  const selfIsSelector = isSelectorService(service);

  return (
    <div data-testid={`draggable-service-${service.id}`}>
      <div
        data-testid={`tree-node-${service.id}`}
        role="button"
        tabIndex={0}
        onClick={() => onSelect(service)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(service);
          }
        }}
        className="flex items-center gap-1"
        style={{ paddingLeft: `${depth * 1.25}rem` }}>
        {/* Test-mode picker: radio for a selector branch, checkbox for a leaf. */}
        {testActive && (parentIsSelector || isLeaf) && (
          <span className="flex-shrink-0 pl-1" onClick={e => e.stopPropagation()}>
            {parentIsSelector ? (
              <input
                type="radio"
                name={`selector-${service.parent}`}
                checked={isPicked}
                data-testid={`test-radio-${service.id}`}
                onChange={() =>
                  service.parent !== null && test?.onSelectorChoice(service.parent, service)
                }
                aria-label={`Выбрать ветку ${service.name}`}
              />
            ) : (
              <Checkbox
                checked={isPicked}
                data-testid={`test-checkbox-${service.id}`}
                onCheckedChange={c => test?.onToggleLeaf(service, c)}
                aria-label={`Выбрать ${service.name}`}
              />
            )}
          </span>
        )}
        {hasChildren ? (
          <button
            data-testid={`tree-expand-${service.id}`}
            onClick={e => {
              e.stopPropagation();
              onToggle(service.id);
            }}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-1"
            aria-label={isExpanded ? 'Свернуть' : 'Развернуть'}
            aria-expanded={isExpanded}>
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        ) : null}

        <span
          className={`flex items-center gap-2 flex-1 min-w-0 text-left px-2 py-1.5 rounded-md transition-colors group ${
            isSelected
              ? 'bg-blue-50 ring-1 ring-blue-200'
              : isHighlighted
                ? 'bg-emerald-50 ring-1 ring-emerald-200'
                : 'hover:bg-gray-50'
          }`}>
          <span className="font-medium text-gray-900 truncate">{service.name}</span>
          <ServiceTypeMarker method={service.calculation_method} testId={`type-marker-node-${service.id}`} />
          <span className="ml-auto hidden lg:flex items-center gap-0.5">
            {isCalculator && (
              <span
                data-testid="add-child-button"
                role="button"
                tabIndex={0}
                onClick={e => {
                  e.stopPropagation();
                  onAddChild(service.id);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    onAddChild(service.id);
                  }
                }}
                className="flex-shrink-0 p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-blue-100 text-gray-400 hover:text-blue-600 transition-opacity"
                aria-label="Добавить дочернюю"
                title="Добавить дочернюю">
                <Plus className="h-3.5 w-3.5" />
              </span>
            )}
          </span>
        </span>
      </div>

      {/* Test-mode inline inputs for a SELECTED leaf (TASK-260). */}
      {testActive && isLeaf && isPicked && (
        <div
          data-testid={`test-inputs-${service.id}`}
          className="ml-8 mt-1 mb-2 rounded-md border border-emerald-100 bg-emerald-50/40 p-2"
          style={{ marginLeft: `${depth * 1.25 + 2}rem` }}
          onClick={e => e.stopPropagation()}>
          <LeafTestInputs service={service} selection={selection} onPatch={test.onPatch} />
        </div>
      )}

      {/* Children (expanded) */}
      {isExpanded && hasChildren && (
        <div className="mt-0.5 space-y-0.5">
          {children.map(child => (
            <TreeNode
              key={child.id}
              service={child}
              childrenByParent={childrenByParent}
              selectedId={selectedId}
              onSelect={onSelect}
              expanded={expanded}
              onToggle={onToggle}
              onAddChild={onAddChild}

              ancestors={nextAncestors}
              depth={depth + 1}
              test={test}
              parentIsSelector={selfIsSelector}
            />
          ))}
        </div>
      )}
    </div>
  );
}
